import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { todayIso } from "@/lib/dates";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  Plus, Trash2, Pencil, ArrowLeftRight, Wrench, Zap, Hammer, Drill, Lightbulb,
  Cog, ClipboardCheck, Camera, AlertTriangle, CheckCircle2, Receipt, DollarSign,
  Briefcase, Loader2, Package, ChevronDown, ShieldAlert, X,
} from "lucide-react";
import { toast } from "sonner";
import { AuthImage } from "@/components/AuthImage";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });

const CATEGORIES = [
  { value: "compactor", label: "Compactor", icon: Cog },
  { value: "generator", label: "Generator", icon: Zap },
  { value: "saw",       label: "Concrete / Cut-off saw", icon: Wrench },
  { value: "drill",     label: "Drill / Demo hammer", icon: Drill },
  { value: "pump",      label: "Pump", icon: Cog },
  { value: "lighting",  label: "Lighting tower", icon: Lightbulb },
  { value: "welder",    label: "Welder", icon: Zap },
  { value: "compressor",label: "Compressor", icon: Cog },
  { value: "ladder",    label: "Ladder / Access", icon: Hammer },
  { value: "hand_tool", label: "Hand tool", icon: Hammer },
  { value: "other",     label: "Other", icon: Package },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));
const CAT_ICON  = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.icon]));

const prettyCat = (v) => {
  if (!v) return "Other";
  if (CAT_LABEL[v]) return CAT_LABEL[v];
  // Custom category: title-case the slug
  return v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};
const iconForCat = (v) => CAT_ICON[v] || Package;

function daysAgo(iso) {
  if (!iso) return 0;
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 86400000;
  return Math.max(0, Math.floor(diff));
}
function daysLabel(iso) {
  const n = daysAgo(iso);
  if (n === 0) return "today";
  if (n === 1) return "1 day ago";
  return `${n} days ago`;
}

async function uploadFile(file, category = "gear") {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", category);
  const { data } = await api.post("/files/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.storage_path;
}

export default function Gear() {
  const { user } = useAuth();
  const isOffice = user.role !== "field";
  const [items, setItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [checkoutItem, setCheckoutItem] = useState(null);
  const [returnItem, setReturnItem] = useState(null);
  const [repairItem, setRepairItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [g, j] = await Promise.all([api.get("/gear"), api.get("/jobs")]);
      setItems(g.data);
      setJobs(j.data);
    } catch { toast.error("Failed to load gear"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => ({
    total: items.length,
    out: items.filter((g) => g.current_checkout).length,
    in_yard: items.filter((g) => !g.current_checkout && (g.condition || "good") === "good").length,
    broken: items.filter((g) => (g.condition || "good") !== "good").length,
    maint_12mo: items.reduce((a, g) => a + (g.last_12mo_maintenance_cost || 0), 0),
  }), [items]);

  const onDelete = async (g) => {
    if (!window.confirm(`Delete "${g.name}"? All checkout history + invoices will be removed.`)) return;
    try {
      await api.delete(`/gear/${g.id}`);
      toast.success("Gear deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="gear-page" className="p-6 lg:p-10 max-w-7xl mx-auto pb-24 lg:pb-10">
      <PageHeader
        tag="/ Small equipment"
        title="Gear"
        subtitle={isOffice
          ? "Compactors, generators, saws — track who has what and what it's costing to maintain."
          : "Grab the gear you need for the job. Return it when you're done."}
        action={isOffice && (
          <Button onClick={() => { setEditing(null); setCreateOpen(true); }}
                  className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold"
                  data-testid="new-gear-button">
            <Plus className="w-4 h-4 mr-2" /> Add gear
          </Button>
        )}
      />

      {/* Office summary */}
      {isOffice && items.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="gear-summary-row">
          <SummaryTile label="Items" value={totals.total} accent="bg-[#09090B]" />
          <SummaryTile label="Out on site" value={totals.out} accent="bg-[#FF5F15]" />
          <SummaryTile label="In yard" value={totals.in_yard} accent="bg-green-600" />
          <SummaryTile label="Broken" value={totals.broken} accent="bg-red-600" emphasise={totals.broken > 0} />
          <SummaryTile label="Maint (12mo)" value={NZD.format(totals.maint_12mo)} accent="bg-yellow-500" mono />
        </div>
      )}

      {loading ? (
        <div className="text-sm text-zinc-500 font-mono">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState title="No gear yet"
          description={isOffice ? "Add your first piece of small equipment to start tracking." : "Your office hasn't added any gear yet."} />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gear-list">
          {items.map((g) => (
            <GearCard
              key={g.id}
              gear={g}
              isOffice={isOffice}
              currentUserId={user.id}
              onTake={() => setCheckoutItem(g)}
              onReturn={() => setReturnItem(g)}
              onRepair={() => setRepairItem(g)}
              onDetails={() => setDetailId(g.id)}
              onEdit={() => { setEditing(g); setCreateOpen(true); }}
              onDelete={() => onDelete(g)}
            />
          ))}
        </div>
      )}

      <GearFormDialog
        key={editing?.id || "new"}
        open={createOpen}
        initial={editing}
        onClose={() => { setCreateOpen(false); setEditing(null); }}
        onSaved={() => { setCreateOpen(false); setEditing(null); load(); }}
      />
      <CheckoutDialog
        gear={checkoutItem}
        jobs={jobs}
        onClose={() => setCheckoutItem(null)}
        onDone={() => { setCheckoutItem(null); load(); }}
      />
      <ReturnDialog
        gear={returnItem}
        onClose={() => setReturnItem(null)}
        onDone={() => { setReturnItem(null); load(); }}
      />
      <MarkRepairedDialog
        gear={repairItem}
        onClose={() => setRepairItem(null)}
        onDone={() => { setRepairItem(null); load(); }}
      />
      <GearDetailDialog
        gearId={detailId}
        isOffice={isOffice}
        onClose={() => setDetailId(null)}
        onChanged={load}
      />
    </div>
  );
}

// ============================================================
//  CARD
// ============================================================
function GearCard({ gear, isOffice, currentUserId, onTake, onReturn, onRepair, onDetails, onEdit, onDelete }) {
  const Icon = iconForCat(gear.category);
  const out = !!gear.current_checkout;
  const heldByMe = out && gear.current_checkout?.user_id === currentUserId;
  const broken = (gear.condition || "good") !== "good";
  const canReturn = out && (isOffice || heldByMe);
  const canTake = !out && !broken;
  const canRepair = broken && !out;
  const ringClass = broken ? "ring-2 ring-red-500" : (heldByMe ? "ring-2 ring-[#FF5F15]" : "");
  const borderClass = broken ? "border-red-500" : "border-zinc-900";

  return (
    <div
      className={`relative bg-white border-2 ${borderClass} shadow-[4px_4px_0_0_#09090B] hover:shadow-[6px_6px_0_0_#FF5F15] transition-shadow ${ringClass}`}
      data-testid={`gear-card-${gear.id}`}
    >
      {/* Header */}
      <div className="p-4 flex items-start gap-3">
        <div className={`w-12 h-12 shrink-0 flex items-center justify-center ${broken ? "bg-red-600 text-white" : out ? "bg-[#FF5F15] text-white" : "bg-zinc-100 text-zinc-700"}`}>
          {gear.photo_path ? (
            <AuthImage path={gear.photo_path} alt={gear.name} className="w-12 h-12 object-cover" />
          ) : (
            <Icon className="w-6 h-6" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-extrabold uppercase tracking-tight text-base leading-tight truncate">
            {gear.name}
          </div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-0.5">
            {prettyCat(gear.category)}
            {gear.serial_number && <span className="text-zinc-400 normal-case font-mono ml-1">· {gear.serial_number}</span>}
          </div>
        </div>
      </div>

      {/* Status block */}
      {broken ? (
        <div className="px-4 pb-3 border-t border-zinc-100 bg-red-500/10" data-testid={`gear-status-${gear.id}`}>
          <div className="pt-3">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-red-700">
              <ShieldAlert className="w-3 h-3" /> Reported broken · {gear.condition === "needs_repair" ? "needs repair" : "damaged"}
            </div>
            {gear.damage_reported_by_name && (
              <div className="mt-1 text-[11px] text-zinc-700 font-mono">
                by <b>{gear.damage_reported_by_name}</b>{gear.damage_reported_at && <> · {daysLabel(gear.damage_reported_at)}</>}
              </div>
            )}
            {gear.damage_notes && (
              <p className="mt-1 text-xs text-zinc-800 bg-white border border-red-200 px-2 py-1 whitespace-pre-wrap" data-testid={`gear-damage-notes-${gear.id}`}>
                "{gear.damage_notes}"
              </p>
            )}
            <div className="text-[10px] font-mono uppercase tracking-widest text-red-700 mt-1.5 font-bold">
              Can't be taken until marked repaired
            </div>
          </div>
        </div>
      ) : (
        <div className={`px-4 pb-3 border-t border-zinc-100 ${out ? "bg-[#FF5F15]/5" : "bg-green-500/5"}`}>
          {out ? (
            <div className="pt-3" data-testid={`gear-status-${gear.id}`}>
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-[#FF5F15]">
                <ArrowLeftRight className="w-3 h-3" /> Out on site
              </div>
              <div className="mt-1 text-sm font-bold">{gear.current_checkout.user_name}{heldByMe && <span className="ml-1.5 text-[10px] bg-[#FF5F15] text-white px-1.5 py-0.5 rounded-sm">YOU</span>}</div>
              {gear.current_checkout.job_title && (
                <div className="mt-0.5 text-xs text-zinc-600 inline-flex items-center gap-1">
                  <Briefcase className="w-3 h-3 text-zinc-400" /> {gear.current_checkout.job_title}
                </div>
              )}
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
                Checked out {daysLabel(gear.current_checkout.checked_out_at)}
              </div>
            </div>
          ) : (
            <div className="pt-3 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-green-700" data-testid={`gear-status-${gear.id}`}>
              <CheckCircle2 className="w-3 h-3" /> In yard · ready to grab
            </div>
          )}
          {isOffice && gear.lifetime_maintenance_cost > 0 && (
            <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              Maint life {NZD.format(gear.lifetime_maintenance_cost)} · 12mo {NZD.format(gear.last_12mo_maintenance_cost)}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="border-t border-zinc-200 px-3 py-2 flex flex-wrap items-center justify-between gap-1 bg-zinc-50">
        <Button size="sm" variant="ghost" onClick={onDetails}
                className="rounded-sm text-[10px] font-bold uppercase tracking-widest text-zinc-600 hover:text-zinc-900"
                data-testid={`gear-details-${gear.id}`}>
          Details
        </Button>
        <div className="flex gap-1 flex-wrap">
          {canRepair && (
            <Button size="sm" onClick={onRepair}
                    className="rounded-sm bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold uppercase tracking-wider h-8"
                    data-testid={`gear-repair-${gear.id}`}>
              <Wrench className="w-3.5 h-3.5 mr-1" /> Mark repaired
            </Button>
          )}
          {canTake && (
            <Button size="sm" onClick={onTake}
                    className="rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white text-[11px] font-bold uppercase tracking-wider h-8"
                    data-testid={`gear-take-${gear.id}`}>
              <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Take it
            </Button>
          )}
          {canReturn && (
            <Button size="sm" onClick={onReturn}
                    className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider h-8"
                    data-testid={`gear-return-${gear.id}`}>
              <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Return
            </Button>
          )}
          {isOffice && (
            <>
              <Button size="icon" variant="outline" onClick={onEdit}
                      className="h-8 w-8 rounded-sm border-zinc-400" data-testid={`gear-edit-${gear.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button size="icon" variant="outline" onClick={onDelete}
                      className="h-8 w-8 rounded-sm border-red-300 text-red-700 hover:bg-red-50"
                      data-testid={`gear-delete-${gear.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, accent, mono, emphasise }) {
  return (
    <div className={`relative bg-white border-2 p-3 sm:p-4 ${emphasise ? "border-red-600" : "border-zinc-900"}`}>
      <div className={`absolute top-0 left-0 h-1 w-12 ${accent}`} />
      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-2 flex items-end gap-1.5">
        <span className={`font-display font-black text-2xl sm:text-3xl leading-none ${mono ? "metric" : ""} ${emphasise ? "text-red-600" : ""}`}>{value}</span>
      </div>
    </div>
  );
}

// ============================================================
//  CREATE / EDIT DIALOG
// ============================================================
function GearFormDialog({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", category: "other", serial_number: "", purchase_date: "",
    purchase_cost: "", hourly_rate: "", photo_path: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [catSuggestions, setCatSuggestions] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/gear/categories").then((r) => setCatSuggestions(r.data || [])).catch(() => {});
    if (initial) {
      setForm({
        name: initial.name || "",
        category: initial.category || "other",
        serial_number: initial.serial_number || "",
        purchase_date: initial.purchase_date || "",
        purchase_cost: initial.purchase_cost ?? "",
        hourly_rate: initial.hourly_rate ?? "",
        photo_path: initial.photo_path || "",
        notes: initial.notes || "",
      });
    } else {
      setForm({ name: "", category: "other", serial_number: "", purchase_date: "", purchase_cost: "", hourly_rate: "", photo_path: "", notes: "" });
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoBusy(true);
    try {
      const p = await uploadFile(f, "gear");
      setForm((s) => ({ ...s, photo_path: p }));
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setPhotoBusy(false); e.target.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        category: (form.category || "").trim().toLowerCase() || "other",
        purchase_cost: form.purchase_cost === "" ? null : Number(form.purchase_cost),
        hourly_rate: form.hourly_rate === "" ? null : Number(form.hourly_rate),
        purchase_date: form.purchase_date || null,
        serial_number: form.serial_number.trim() || null,
        photo_path: form.photo_path || null,
        notes: form.notes.trim() || null,
      };
      if (initial) {
        await api.patch(`/gear/${initial.id}`, payload);
        toast.success("Gear updated");
      } else {
        await api.post("/gear", payload);
        toast.success("Gear added");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="gear-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">
            {initial ? "Edit gear" : "Add gear"}
          </DialogTitle>
          <DialogDescription>Compactors, generators, saws — anything that gets shared around.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="label-tag">Name *</Label>
            <Input value={form.name} onChange={set("name")} className="mt-2 rounded-sm border-zinc-400"
                   placeholder="e.g. Honda EU30i Generator" required
                   data-testid="gear-form-name" />
          </div>
          <div>
            <Label className="label-tag">Category</Label>
            <Input
              list="gear-category-options"
              value={form.category}
              onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))}
              placeholder="Pick from list or type your own"
              className="mt-2 rounded-sm border-zinc-400"
              data-testid="gear-cat-input"
            />
            <datalist id="gear-category-options">
              {catSuggestions.map((c) => (
                <option key={c} value={c}>{prettyCat(c)}</option>
              ))}
            </datalist>
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-1">
              Start typing — pick a suggestion or invent your own.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Serial / asset #</Label>
              <Input value={form.serial_number} onChange={set("serial_number")} className="mt-2 rounded-sm border-zinc-400" data-testid="gear-serial-input" />
            </div>
            <div>
              <Label className="label-tag">Purchase cost (NZD)</Label>
              <Input type="number" step="0.01" min="0" value={form.purchase_cost} onChange={set("purchase_cost")}
                     className="mt-2 rounded-sm border-zinc-400" data-testid="gear-cost-input" />
            </div>
          </div>
          <div>
            <Label className="label-tag">Purchase date</Label>
            <Input type="date" value={form.purchase_date} onChange={set("purchase_date")} className="mt-2 rounded-sm border-zinc-400" data-testid="gear-date-input" />
          </div>
          <div>
            <Label className="label-tag">Charge-out rate ($/hr)</Label>
            <Input type="number" step="0.01" min="0" value={form.hourly_rate} onChange={set("hourly_rate")}
                   placeholder="e.g. 15.00" className="mt-2 rounded-sm border-zinc-400" data-testid="gear-rate-input" />
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-1">
              Used to cost gear hours from timesheets against jobs.
            </p>
          </div>
          <div>
            <Label className="label-tag">Photo</Label>
            <div className="mt-2 flex items-center gap-3">
              {form.photo_path ? (
                <AuthImage path={form.photo_path} alt="gear" className="w-16 h-16 object-cover border border-zinc-300" />
              ) : (
                <div className="w-16 h-16 bg-zinc-100 border border-zinc-300 flex items-center justify-center text-zinc-400">
                  <Package className="w-6 h-6" />
                </div>
              )}
              <label className="flex items-center gap-2 border border-dashed border-zinc-400 px-3 py-2 cursor-pointer hover:border-[#FF5F15] hover:text-[#FF5F15]">
                <Camera className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">{photoBusy ? "Uploading…" : (form.photo_path ? "Change" : "Upload")}</span>
                <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="gear-photo-input" />
              </label>
            </div>
          </div>
          <div>
            <Label className="label-tag">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={set("notes")} className="mt-2 rounded-sm border-zinc-400" data-testid="gear-notes-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase tracking-wider font-bold" data-testid="gear-form-save">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : (initial ? "Save" : "Add gear")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
//  CHECKOUT DIALOG
// ============================================================
function CheckoutDialog({ gear, jobs, onClose, onDone }) {
  const [jobId, setJobId] = useState("none");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setJobId("none"); }, [gear]);

  const submit = async () => {
    if (!gear) return;
    setBusy(true);
    try {
      await api.post(`/gear/${gear.id}/checkout`, { job_id: jobId === "none" ? null : jobId });
      toast.success(`${gear.name} checked out to you`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!gear} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="gear-checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Take it</DialogTitle>
          <DialogDescription>You're checking out <b>{gear?.name}</b>. Optionally tag the job it's for.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-tag">Job (optional)</Label>
            <Select value={jobId} onValueChange={setJobId}>
              <SelectTrigger className="mt-2 rounded-sm border-zinc-400" data-testid="gear-checkout-job">
                <SelectValue placeholder="No job — yard / general use" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No job — yard / general use</SelectItem>
                {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white uppercase tracking-wider font-bold" data-testid="gear-checkout-confirm">
            {busy ? "Checking out…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
//  RETURN DIALOG
// ============================================================
function ReturnDialog({ gear, onClose, onDone }) {
  const [condition, setCondition] = useState("good");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setCondition("good"); setNotes(""); }, [gear]);

  const submit = async () => {
    if (!gear) return;
    setBusy(true);
    try {
      await api.post(`/gear/${gear.id}/return`, { condition, notes: notes.trim() || null });
      toast.success("Returned");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Return failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!gear} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="gear-return-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Return {gear?.name}</DialogTitle>
          <DialogDescription>Tell the office what condition it's coming back in.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-tag">Condition</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {[
                { v: "good", label: "Good", icon: CheckCircle2, cls: "border-green-500 hover:bg-green-50 data-[on=true]:bg-green-600 data-[on=true]:text-white data-[on=true]:border-green-600" },
                { v: "damaged", label: "Damaged", icon: AlertTriangle, cls: "border-yellow-500 hover:bg-yellow-50 data-[on=true]:bg-yellow-500 data-[on=true]:text-white data-[on=true]:border-yellow-500" },
                { v: "needs_repair", label: "Needs repair", icon: Wrench, cls: "border-red-500 hover:bg-red-50 data-[on=true]:bg-red-600 data-[on=true]:text-white data-[on=true]:border-red-600" },
              ].map((opt) => {
                const Icon = opt.icon;
                const on = condition === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    data-on={on}
                    onClick={() => setCondition(opt.v)}
                    className={`p-3 border-2 transition-colors flex flex-col items-center gap-1 text-xs font-bold uppercase tracking-wider ${opt.cls}`}
                    data-testid={`gear-return-cond-${opt.v}`}
                  >
                    <Icon className="w-4 h-4" />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="label-tag">Notes {condition !== "good" && <span className="text-red-600 normal-case font-normal">(highly recommended)</span>}</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder={condition === "good" ? "Optional comments…" : "What broke? What needs fixing?"}
                      className="mt-2 rounded-sm border-zinc-400" data-testid="gear-return-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="gear-return-confirm">
            {busy ? "Returning…" : "Confirm return"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
//  DETAIL DIALOG (history + invoices)
// ============================================================
function GearDetailDialog({ gearId, isOffice, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [repairOpen, setRepairOpen] = useState(false);

  const refresh = async () => {
    if (!gearId) { setData(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/gear/${gearId}`);
      setData(data);
    } catch { toast.error("Failed to load"); onClose(); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [gearId]);

  const deleteInvoice = async (iid) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await api.delete(`/gear/invoices/${iid}`);
      toast.success("Invoice removed");
      refresh();
      onChanged?.();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <Dialog open={!!gearId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="gear-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">
            {data?.name || "Gear"}
          </DialogTitle>
          {data && (
            <DialogDescription>
              {CAT_LABEL[data.category] || data.category}
              {data.serial_number && <> · <span className="font-mono">{data.serial_number}</span></>}
            </DialogDescription>
          )}
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-sm text-zinc-500 font-mono">Loading…</div>
        ) : data ? (
          <div className="space-y-5">
            {/* Damage banner (highest priority) */}
            {(data.condition || "good") !== "good" && (
              <div className="border-2 border-red-500 bg-red-50 p-3" data-testid="gear-detail-damage-banner">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-red-700 flex items-center gap-1.5">
                      <ShieldAlert className="w-3 h-3" /> Reported broken · {data.condition === "needs_repair" ? "needs repair" : "damaged"}
                    </div>
                    {data.damage_reported_by_name && (
                      <div className="text-[11px] font-mono text-zinc-700 mt-1">
                        by <b>{data.damage_reported_by_name}</b>
                        {data.damage_reported_at && <> · {new Date(data.damage_reported_at).toLocaleString("en-NZ")}</>}
                      </div>
                    )}
                    {data.damage_notes && (
                      <p className="mt-2 text-sm text-zinc-800 bg-white border border-red-200 px-2 py-1 whitespace-pre-wrap">
                        "{data.damage_notes}"
                      </p>
                    )}
                  </div>
                  <Button size="sm" onClick={() => setRepairOpen(true)}
                          className="rounded-sm bg-green-600 hover:bg-green-700 text-white uppercase tracking-wider font-bold text-xs"
                          data-testid="gear-detail-mark-repaired">
                    <Wrench className="w-3.5 h-3.5 mr-1" /> Mark repaired
                  </Button>
                </div>
              </div>
            )}

            {/* Current status */}
            <div className={`border-2 p-3 ${data.current_checkout ? "border-[#FF5F15] bg-[#FF5F15]/5" : (data.condition || "good") !== "good" ? "border-zinc-300 bg-zinc-50" : "border-green-500 bg-green-50"}`}>
              {data.current_checkout ? (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF5F15]">Out on site</div>
                  <div className="mt-1 font-bold">{data.current_checkout.user_name}</div>
                  {data.current_checkout.job_title && (
                    <div className="text-xs text-zinc-600 inline-flex items-center gap-1 mt-0.5">
                      <Briefcase className="w-3 h-3" /> {data.current_checkout.job_title}
                    </div>
                  )}
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
                    Since {new Date(data.current_checkout.checked_out_at).toLocaleString("en-NZ")} · {daysLabel(data.current_checkout.checked_out_at)}
                  </div>
                </div>
              ) : (data.condition || "good") !== "good" ? (
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Locked — needs repair before re-use
                </div>
              ) : (
                <div className="text-[10px] font-bold uppercase tracking-widest text-green-700">In yard · ready to grab</div>
              )}
            </div>

            {/* Maintenance summary */}
            {isOffice && (
              <div className="grid grid-cols-3 gap-2">
                <SummaryTile label="Purchase" value={data.purchase_cost ? NZD.format(data.purchase_cost) : "—"} accent="bg-zinc-900" mono />
                <SummaryTile label="Maint 12mo" value={NZD.format(data.last_12mo_maintenance_cost || 0)} accent="bg-yellow-500" mono />
                <SummaryTile label="Maint life" value={NZD.format(data.lifetime_maintenance_cost || 0)} accent="bg-red-600" mono />
              </div>
            )}

            {/* Repairs history */}
            {Array.isArray(data.repairs) && data.repairs.length > 0 && (
              <Section title={`Repair log · ${data.repairs.length}`}>
                <div className="space-y-2">
                  {[...data.repairs].reverse().map((r) => (
                    <div key={r.id} className="border border-green-300 bg-green-50 p-2" data-testid={`gear-repair-entry-${r.id}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-green-700 flex items-center gap-1.5">
                          <Wrench className="w-3 h-3" /> Marked repaired
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                          {new Date(r.repaired_at).toLocaleString("en-NZ")}
                        </div>
                      </div>
                      <div className="text-xs text-zinc-700 mt-0.5">
                        by <b>{r.repaired_by_name}</b>
                        {r.previous_condition && <span className="text-zinc-500"> · was {r.previous_condition.replace("_", " ")}</span>}
                      </div>
                      {r.notes && (
                        <p className="mt-1 text-sm text-zinc-800 whitespace-pre-wrap">{r.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Checkout history */}
            <Section title={`Checkout history · ${data.history.length}`}>
              {data.history.length === 0 ? (
                <div className="text-sm text-zinc-500 italic">No history yet.</div>
              ) : (
                <div className="space-y-2">
                  {data.history.map((h) => (
                    <div key={h.id} className="border border-zinc-300 bg-white p-3" data-testid={`gear-history-${h.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <div className="font-bold text-sm">{h.user_name}</div>
                          {h.job_title && <div className="text-xs text-zinc-600 inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {h.job_title}</div>}
                          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
                            {new Date(h.checked_out_at).toLocaleString("en-NZ")} → {h.returned_at ? new Date(h.returned_at).toLocaleString("en-NZ") : "still out"}
                          </div>
                        </div>
                        {h.return_condition && (
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${
                            h.return_condition === "good" ? "bg-green-100 text-green-800 border border-green-300" :
                            h.return_condition === "damaged" ? "bg-yellow-100 text-yellow-800 border border-yellow-300" :
                            "bg-red-100 text-red-800 border border-red-300"
                          }`}>
                            {h.return_condition.replace("_", " ")}
                          </span>
                        )}
                      </div>
                      {h.return_notes && (
                        <p className="mt-2 text-sm text-zinc-800 bg-zinc-50 border-l-2 border-zinc-300 pl-2 py-1 whitespace-pre-wrap">
                          {h.return_notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Invoices */}
            {isOffice && (
              <Section
                title={`Maintenance invoices · ${data.invoices.length}`}
                action={
                  <Button size="sm" onClick={() => setInvOpen(true)}
                          className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[10px] font-bold uppercase tracking-wider h-8"
                          data-testid="gear-add-invoice-button">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add invoice
                  </Button>
                }
              >
                {data.invoices.length === 0 ? (
                  <div className="text-sm text-zinc-500 italic">No invoices logged yet.</div>
                ) : (
                  <div className="space-y-2">
                    {data.invoices.map((inv) => (
                      <div key={inv.id} className="border border-zinc-300 bg-white p-3 flex items-start justify-between gap-3" data-testid={`gear-invoice-${inv.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-sm">{inv.vendor}</span>
                            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{inv.date}</span>
                          </div>
                          {inv.description && (
                            <p className="mt-1 text-xs text-zinc-700 whitespace-pre-wrap">{inv.description}</p>
                          )}
                          {inv.receipt_path && (
                            <AuthImage path={inv.receipt_path} alt="Receipt" className="mt-2 w-20 h-20 object-cover border border-zinc-200" />
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-display font-black text-lg leading-none metric">{NZD.format(inv.amount)}</div>
                          <Button size="icon" variant="ghost" onClick={() => deleteInvoice(inv.id)} className="h-7 w-7 mt-1 rounded-sm text-zinc-500 hover:text-red-600">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            )}

            {data.notes && (
              <Section title="Notes">
                <p className="text-sm text-zinc-800 whitespace-pre-wrap">{data.notes}</p>
              </Section>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold" data-testid="gear-detail-close">
            Close
          </Button>
        </DialogFooter>

        <AddInvoiceDialog open={invOpen} gear={data} onClose={() => setInvOpen(false)} onSaved={() => { setInvOpen(false); refresh(); onChanged?.(); }} />
        <MarkRepairedDialog gear={repairOpen ? data : null} onClose={() => setRepairOpen(false)} onDone={() => { setRepairOpen(false); refresh(); onChanged?.(); }} />
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children, action }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display font-extrabold uppercase tracking-tight text-sm flex items-center gap-1.5">
          <ChevronDown className="w-3 h-3" /> {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

// ============================================================
//  ADD INVOICE DIALOG
// ============================================================
function AddInvoiceDialog({ open, gear, onClose, onSaved }) {
  const today = todayIso();
  const [form, setForm] = useState({ date: today, vendor: "", amount: "", description: "", receipt_path: "" });
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({ date: today, vendor: "", amount: "", description: "", receipt_path: "" });
    // eslint-disable-next-line
  }, [open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const onReceipt = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoBusy(true);
    try {
      const p = await uploadFile(f, "gear_receipt");
      setForm((s) => ({ ...s, receipt_path: p }));
      toast.success("Receipt uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setPhotoBusy(false); e.target.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vendor.trim()) { toast.error("Vendor is required"); return; }
    if (form.amount === "" || Number(form.amount) < 0) { toast.error("Amount required"); return; }
    setBusy(true);
    try {
      await api.post(`/gear/${gear.id}/invoices`, {
        date: form.date, vendor: form.vendor.trim(),
        amount: Number(form.amount),
        description: form.description.trim() || null,
        receipt_path: form.receipt_path || null,
      });
      toast.success("Invoice added");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="gear-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2">
            <Receipt className="w-5 h-5" /> Add invoice
          </DialogTitle>
          <DialogDescription>Log a maintenance / repair invoice for {gear?.name}.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Date</Label>
              <Input type="date" value={form.date} onChange={set("date")} className="mt-2 rounded-sm border-zinc-400" required data-testid="inv-date-input" />
            </div>
            <div>
              <Label className="label-tag">Amount NZD</Label>
              <div className="mt-2 relative">
                <DollarSign className="absolute w-4 h-4 left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input type="number" step="0.01" min="0" value={form.amount} onChange={set("amount")}
                       className="pl-8 rounded-sm border-zinc-400" required data-testid="inv-amount-input" />
              </div>
            </div>
          </div>
          <div>
            <Label className="label-tag">Vendor</Label>
            <Input value={form.vendor} onChange={set("vendor")} className="mt-2 rounded-sm border-zinc-400"
                   placeholder="e.g. Repco, Hire-King, mate down the road" required data-testid="inv-vendor-input" />
          </div>
          <div>
            <Label className="label-tag">Description</Label>
            <Textarea rows={2} value={form.description} onChange={set("description")} className="mt-2 rounded-sm border-zinc-400"
                      placeholder="e.g. New pull cord + spark plug" data-testid="inv-desc-input" />
          </div>
          <div>
            <Label className="label-tag">Receipt photo (optional)</Label>
            <div className="mt-2 flex items-center gap-3">
              {form.receipt_path ? (
                <AuthImage path={form.receipt_path} alt="receipt" className="w-12 h-12 object-cover border border-zinc-300" />
              ) : (
                <div className="w-12 h-12 bg-zinc-100 border border-zinc-300 flex items-center justify-center text-zinc-400">
                  <Receipt className="w-5 h-5" />
                </div>
              )}
              <label className="flex items-center gap-2 border border-dashed border-zinc-400 px-3 py-1.5 cursor-pointer hover:border-[#FF5F15] hover:text-[#FF5F15]">
                <Camera className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">{photoBusy ? "Uploading…" : (form.receipt_path ? "Change" : "Upload")}</span>
                <input type="file" accept="image/*" className="hidden" onChange={onReceipt} data-testid="inv-receipt-input" />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="inv-save">
              {busy ? "Saving…" : "Save invoice"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
//  MARK REPAIRED DIALOG
// ============================================================
function MarkRepairedDialog({ gear, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setNotes(""); }, [gear]);

  const submit = async () => {
    if (!gear) return;
    setBusy(true);
    try {
      await api.post(`/gear/${gear.id}/mark_repaired`, { notes: notes.trim() || null });
      toast.success("Marked as repaired — gear can be taken again");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!gear) return null;
  return (
    <Dialog open={!!gear} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="gear-repair-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2">
            <Wrench className="w-5 h-5" /> Mark as repaired
          </DialogTitle>
          <DialogDescription>
            Confirm that <b>{gear.name}</b> is back in working order. Once marked,
            it'll be available for anyone to grab again.
          </DialogDescription>
        </DialogHeader>
        {gear.damage_notes && (
          <div className="border-l-2 border-red-500 bg-red-50 p-2 text-xs">
            <div className="label-tag mb-1">Original report</div>
            <p className="text-zinc-800 whitespace-pre-wrap">"{gear.damage_notes}"</p>
            {gear.damage_reported_by_name && (
              <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">
                — {gear.damage_reported_by_name}
              </p>
            )}
          </div>
        )}
        <div>
          <Label className="label-tag">What was done? <span className="text-zinc-400 normal-case font-normal">(optional)</span></Label>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Replaced pull cord, cleaned carby, all good now"
                    className="mt-2 rounded-sm border-zinc-400"
                    data-testid="gear-repair-notes" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-wider font-bold">Cancel</Button>
          <Button onClick={submit} disabled={busy}
                  className="rounded-sm bg-green-600 hover:bg-green-700 text-white uppercase tracking-wider font-bold"
                  data-testid="gear-repair-confirm">
            {busy ? "Saving…" : "Confirm repaired"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
